import { Button } from "@/components/ui/Button";
import { useState } from "react";
import {
  CreateTableQueryBuilder,
  TableBuilderData,
} from "@/components/features/table-builder/CreateTableQueryBuilder";
import { FicMappingDropDown } from "./EditTableQueryBuilder";
import { useCreateMemberTemplateTable } from "@/hooks/mutations/useTableMutations";

const ficMappings: FicMappingDropDown[] = [
  {
    value: "policy_number",
    label: "Policy Number",
    description: "A unique identifier assigned to each insurance policy.",
    presets: { dataType: "VARCHAR(255)", isNullable: false },
  },
  {
    value: "original_effective_date",
    label: "Original Effective Date",
    description: "A unique identifier assigned to each insurance policy.",
    presets: { dataType: "DATE", isNullable: false },
  },
  {
    value: "agent_matcher",
    label: "Agent Matcher",
    description: "A unique identifier assigned to each agent.",
    presets: { dataType: "VARCHAR(255)", isNullable: false },
  },
  {
    value: "lives",
    label: "Lives",
    description: "The number of lives covered in the policy.",
    presets: { dataType: "INT", isNullable: false },
  },
];

export const GetStarted = () => {
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const createTemplateMutation = useCreateMemberTemplateTable();

  const handleTemplateSubmit = (data: TableBuilderData) => {
    createTemplateMutation.mutate(
      { columns: data.columns },
      {
        onSuccess: () => {
          setCurrentPageIndex(pages.length - 1);
        },
      },
    );
  };

  const pages = [
    {
      title: "Welcome to Roca!",
      description: "",
      content: (
        <div>
          <p className="text-lg text-neutral-800 dark:text-neutral-300 mb-6">
            Let&apos;s get you set up for success! This quick initial
            configuration will help you get the most out of Roca.
          </p>
          <p className="text-neutral-800 dark:text-neutral-400 mb-4">
            We&apos;ll guide you through thess steps:
          </p>
          <ol className="list-decimal pl-6 text-neutral-800 dark:text-neutral-400 space-y-3 mb-6">
            <li>
              <strong className="font-semibold dark:text-neutral-200">
                Introduction:
              </strong>{" "}
              Discover the power of Roca.
            </li>
            <li>
              <strong className="font-semibold dark:text-neutral-200">
                Define Contract Template:
              </strong>{" "}
              Design the structure for your sold contracts.
            </li>
            <li>
              <strong className="font-semibold dark:text-neutral-200">
                Confirmation:
              </strong>{" "}
              Review and jump into your dashboard!
            </li>
          </ol>
          <p className="text-neutral-800 dark:text-neutral-300 mb-4">
            Ready to begin your Roca journey? Click on &quot;Next&quot;
          </p>
        </div>
      ),
    },
    {
      title: "Sold Contract Template Setup",
      description:
        "To get started, let's set up the Sold Contract template. This is the standard spreadsheet format used to record committed payments from customers, facilitated by our agents.",
      content: (
        <>
          <p className="text-lg text-neutral-800 dark:text-neutral-300 mb-6">
            <strong>Important!</strong> Order matters when creating a sold
            contract template. Make sure your template closely matches your
            spreadsheet&apos;s structure.
          </p>
          <CreateTableQueryBuilder
            onSubmit={handleTemplateSubmit}
            isPending={createTemplateMutation.isPending}
            type="member-template"
            mappings={ficMappings}
          />
        </>
      ),
    },
    {
      title: "All Set!",
      description: "Ready to go.",
      content: (
        <div className="bg-white rounded-lg dark:bg-neutral-900">
          <p className="text-lg text-neutral-800 dark:text-neutral-300 mb-6">
            Fantastic! You&apos;ve completed the initial setup for{" "}
            <strong>Roca</strong>.
          </p>
          <p className="text-neutral-800 dark:text-neutral-400 mb-4">
            Everything is now <strong>configured</strong> and{" "}
            <strong>ready for you.</strong>
          </p>
          <p className="text-neutral-800 dark:text-neutral-300 mb-4">
            Click on &quot;Finish&quot; to head to your dashboard and start
            exploring.
          </p>
        </div>
      ),
    },
  ];

  const currentPage = pages[currentPageIndex];
  const isFirstPage = currentPageIndex === 0;
  const isLastPage = currentPageIndex === pages.length - 1;

  const handleNext = () => {
    if (currentPageIndex < pages.length - 1) {
      setCurrentPageIndex((prev) => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (currentPageIndex > 0) {
      setCurrentPageIndex((prev) => prev - 1);
    }
  };

  return (
    <div className="h-full p-6 flex flex-col justify-between">
      <div>
        <h2 className="text-lg font-semibold tracking-tight">
          {currentPage.title}
        </h2>
        <p className="text-sm text-muted-foreground">
          {currentPage.description}
        </p>
        <div className="py-4">{currentPage.content}</div>
      </div>
      <div className="flex justify-between">
        <Button
          variant="outline"
          onClick={handlePrevious}
          disabled={isFirstPage || isLastPage}
        >
          Previous
        </Button>
        <Button
          disabled={currentPageIndex === 1}
          onClick={isLastPage ? () => window.location.reload() : handleNext}
        >
          {isLastPage ? "Finish" : "Next"}
        </Button>
      </div>
    </div>
  );
};
